import moment from "moment";
import UserService from "./UserService";
import UtilityService from "./UtilityService";
import ConfigurationService from "./ConfigurationService";
import { postToAgents } from "./PlaybookApi";

const ATTACHMENT_VIEW_PATH = "/playbooks/runs/books/attachment_view";
const AS_OF_FORMAT = "MMM D, h:mm a";
const CHANGE_DATE_FORMAT = "MMM D, YYYY";
const FACT_STATUS_DESCRIPTIONS = {
	verified: { tone: "verified", label: "Found in the source" },
	found_in_document: { tone: "verified", label: "Found in the document" },
	not_in_source: { tone: "failing", label: "Not in the source" },
	row_not_found: { tone: "failing", label: "The cited row is not in what the agent read" },
	wrong_tool: { tone: "failing", label: "Cited from a different source than the one read" },
	invalid: { tone: "failing", label: "Not a figure that can be checked" },
	missing: { tone: "failing", label: "Not stated" },
	unverifiable: { tone: "none", label: "Cannot be checked against a source" },
};
const CALCULATED_BY_CODE = { tone: "code", label: "Calculated by code" };
const UNKNOWN_STATUS = { tone: "none", label: "Not checked" };
const QUICKBOOKS_ENTITY_TYPE_BY_LEDGER_LABEL = {
	Bill: "Bill",
	Expense: "Purchase",
	Check: "Purchase",
	"Credit Card Expense": "Purchase",
	"Journal Entry": "JournalEntry",
	Deposit: "Deposit",
	"Vendor Credit": "VendorCredit",
	Invoice: "Invoice",
	"Credit Memo": "CreditMemo",
	"Sales Receipt": "SalesReceipt",
	"Purchase Order": "PurchaseOrder",
	Estimate: "Estimate",
	Transfer: "Transfer",
	"Bill Payment": "BillPayment",
	"Receive Payment": "Payment",
	"Refund Receipt": "RefundReceipt",
};
const READABLE_QUICKBOOKS_ENTITY_TYPES = ["Purchase", "Bill", "JournalEntry", "Deposit", "Invoice", "SalesReceipt", "Payment", "BillPayment", "Transfer", "VendorCredit", "CreditMemo"];
const VIEWER_KIND = {
	ledger: "ledger",
	transaction: "transaction",
	attachments: "attachments",
	trialBalance: "trial_balance",
	document: "document",
};
const BOOKS_TAB_LABELS = {
	frozen: "What the agent read",
	ledger: "Ledger now",
	trialBalance: "Trial balance",
	transaction: "Transaction",
	document: "Document",
};
const DRIVE_FILE_ADDRESS = "https://drive.google.com/file/d/";

const PlaybookBooksService = {
	/**
	 * One account's ledger as QuickBooks holds it now, for the client of a playbook run, the run's
	 * month unless both days of another period are given. Each entry comes back marked isCited when
	 * it is one of the rows the finding cited. A ledger row is matched to a cited row by its
	 * transaction type and transaction id together (ledgerEntryKey), never the id alone, because QuickBooks gives
	 * the same id to transactions of different types (a Bill and a Payment can both be 42). Rejects
	 * with the server's words when the run cannot be read, for example when QuickBooks needs
	 * reconnecting.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @param {String} params.qboAccountId
	 * @param {String} [params.periodStart] YYYY-MM-DD, given together with periodEnd
	 * @param {String} [params.periodEnd] YYYY-MM-DD, given together with periodStart
	 * @param {Array} [params.citedRows] [{ transactionType, transactionId }] from the finding's proof
	 * @return {Promise<Object>} { asOf, qboAccountId, periodStart, periodEnd, opening, closing, entries: [{ date, transactionType, transactionId, number, name, memo, split, amount, balance, isCited }], changesSinceRun }
	 */
	async readLedger({ runId, qboAccountId, periodStart, periodEnd, citedRows = [] }) {
		const ledger = await postToAgents({ operationId: "readPlaybookRunLedger", body: { runId, qboAccountId, periodStart, periodEnd } });
		const citedKeys = citedRows.map(row => this.ledgerEntryKey({ transactionType: row.transactionType, transactionId: row.transactionId }));
		const citedKeySet = new Set(citedKeys.filter(Boolean));
		const markedEntries = ledger.entries.map(entry => {
			const entryKey = this.ledgerEntryKey({ transactionType: entry.transactionType, transactionId: entry.transactionId });
			const isCited = !!entryKey && citedKeySet.has(entryKey);
			const markedEntry = { ...entry, isCited };
			return markedEntry;
		});
		const markedLedger = { ...ledger, entries: markedEntries };
		return markedLedger;
	},

	/**
	 * The trial balance as QuickBooks holds it now for the client of a playbook run, from the first
	 * day of the client's fiscal year to the run's period end. Rejects with the server's words.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @return {Promise<Object>} { asOf, periodStart, periodEnd, rows: [{ qboAccountId, name, debitCents, creditCents }] }
	 */
	readTrialBalance({ runId }) {
		return postToAgents({ operationId: "readPlaybookRunTrialBalance", body: { runId } });
	},

	/**
	 * One QuickBooks transaction of a playbook run's client, read now, by the label the general
	 * ledger gives it ("Bill", "Expense", "Journal Entry") and its id. Rejects with the server's
	 * words, including for a kind of transaction that cannot be opened.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @param {String} params.transactionType the ledger label
	 * @param {String} params.transactionId
	 * @return {Promise<Object>} { asOf, transactionType, entityType, transactionId, date, number, payee, memo, total, lines: [{ lineNumber, detailType, account, item, amount, description, postingType, taxCode }] }
	 */
	readTransaction({ runId, transactionType, transactionId }) {
		return postToAgents({ operationId: "readPlaybookRunTransaction", body: { runId, transactionType, transactionId } });
	},

	/**
	 * The files QuickBooks holds on one transaction of a playbook run's client. checked is false
	 * when that kind of transaction cannot be checked for files, which is not the same as having
	 * none.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @param {String} params.transactionType the ledger label
	 * @param {String} params.transactionId
	 * @return {Promise<Object>} { checked, attachments: [{ id, fileName, contentType, sizeBytes }] }
	 */
	listAttachments({ runId, transactionType, transactionId }) {
		return postToAgents({ operationId: "listPlaybookRunAttachments", body: { runId, transactionType, transactionId } });
	},

	/**
	 * The address that shows one file attached to a run's transaction inline, for an iframe. The
	 * sign-in token goes in the address because an iframe cannot send headers; the server refuses a
	 * file QuickBooks does not list on that transaction.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @param {String} params.transactionType the ledger label
	 * @param {String} params.transactionId
	 * @param {String} params.attachableId
	 * @return {String}
	 */
	attachmentViewUrl({ runId, transactionType, transactionId, attachableId }) {
		const { authToken } = UserService.getUserData();
		const query = new URLSearchParams({ runId, transactionType, transactionId, attachableId, authToken });
		const address = `${ConfigurationService.resolveApi().rest}${ATTACHMENT_VIEW_PATH}?${query.toString()}`;
		return address;
	},

	/**
	 * The one key that names a ledger line's transaction: its ledger label and its QuickBooks id
	 * together, as "Bill|42", because QuickBooks reuses an id across transaction types. Every place
	 * that matches a live ledger line to a cited row uses this key. Null when either part is missing,
	 * so a row with no transaction never matches another.
	 *
	 * @param {Object} params
	 * @param {String|null} params.transactionType the ledger label
	 * @param {String|Number|null} params.transactionId
	 * @return {String|null}
	 */
	ledgerEntryKey({ transactionType, transactionId }) {
		const hasType = !!transactionType;
		const hasId = transactionId !== null && transactionId !== undefined && String(transactionId) !== "";
		if (!hasType || !hasId) return null;
		const entryKey = `${transactionType}|${transactionId}`;
		return entryKey;
	},

	/**
	 * The tabs of the books panel for one card, in a fixed order, built only from what the cited
	 * rows say a reviewer can open (each row's viewers, worked out by the server from the tool's own
	 * viewer declarations), never from a tool's or a playbook's name.
	 *
	 * "What the agent read" (frozen) is always first, even with no evidence. "Ledger now" is offered
	 * for the card's own QuickBooks account when one is given (a journal proposal's line account has
	 * no cited row), otherwise for the account a ledger viewer names, with the cited lines' keys
	 * (ledgerEntryKey) for highlighting. "Trial balance" is offered when a cited row has a trial
	 * balance viewer, and for the card's own QuickBooks account the same way the ledger is (a code
	 * check's finding cites no rows but names its account); it reads to the run's period end, so
	 * only the account to highlight is passed.
	 * "Transaction" holds the transaction and its attachments, for the first cited row with either
	 * viewer, and only when that kind of transaction can be opened or checked for files. That
	 * follows the server's own lists: the ledger label is translated to QuickBooks's entity name by
	 * the map in workflow-api services/qboTrialBalanceService.js
	 * (qboEntityTypeOfLedgerTransactionType), a transaction is opened only for the entity types in
	 * READABLE_QUICKBOOKS_ENTITY_TYPES of workflow-api services/agentRunBooksService.js, and files
	 * can be listed for any label the map knows. Both lists are mirrored here and must change with
	 * the server's. "Document" links out to the cited Drive file, because the document view route
	 * opens Workflow documents, not Drive files.
	 *
	 * @param {Object} params
	 * @param {Object|null} params.evidence from PlaybookReviewService.getFindingEvidence, { proof: { citedRows: [{ transactionType, transactionId, values, viewers: [{ kind, args }] }] } }
	 * @param {String|null} [params.qboAccountId] the card's QuickBooks account
	 * @return {Array} [{ key: "frozen" }, { key: "ledger", qboAccountId, citedEntryKeys }, { key: "trialBalance", qboAccountId }, { key: "transaction", transactionType, transactionId, canReadTransaction, canListAttachments }, { key: "document", driveFileId, fileName, driveUrl }], each with its label
	 */
	describeBooksTabs({ evidence, qboAccountId = null }) {
		const proof = evidence && evidence.proof ? evidence.proof : null;
		const citedRows = proof && Array.isArray(proof.citedRows) ? proof.citedRows : [];
		const viewersOfRow = row => (Array.isArray(row.viewers) ? row.viewers : []);
		const firstViewerOfKind = kind => {
			const rowWithViewer = citedRows.find(row => viewersOfRow(row).some(viewer => viewer.kind === kind));
			if (!rowWithViewer) return null;
			const viewer = viewersOfRow(rowWithViewer).find(candidate => candidate.kind === kind);
			const found = { row: rowWithViewer, args: viewer.args || {} };
			return found;
		};
		const tabs = [{ key: "frozen", label: BOOKS_TAB_LABELS.frozen }];

		const ledgerViewer = firstViewerOfKind(VIEWER_KIND.ledger);
		const viewerAccountId = ledgerViewer ? ledgerViewer.args.qboAccountId : null;
		const cardAccountId = qboAccountId ? String(qboAccountId) : null;
		const ledgerAccountId = cardAccountId || viewerAccountId;
		if (ledgerAccountId) {
			const entryKeys = citedRows.map(row => this.ledgerEntryKey({ transactionType: row.transactionType, transactionId: row.transactionId }));
			const citedEntryKeys = [...new Set(entryKeys.filter(Boolean))];
			tabs.push({ key: "ledger", label: BOOKS_TAB_LABELS.ledger, qboAccountId: ledgerAccountId, citedEntryKeys });
		}

		const trialBalanceViewer = firstViewerOfKind(VIEWER_KIND.trialBalance);
		const hasTrialBalanceToShow = !!trialBalanceViewer || !!cardAccountId;
		if (hasTrialBalanceToShow) tabs.push({ key: "trialBalance", label: BOOKS_TAB_LABELS.trialBalance, qboAccountId: ledgerAccountId });

		const transactionViewer = firstViewerOfKind(VIEWER_KIND.transaction) || firstViewerOfKind(VIEWER_KIND.attachments);
		if (transactionViewer) {
			const { transactionType, transactionId } = transactionViewer.args;
			const isKnownLabel = Object.prototype.hasOwnProperty.call(QUICKBOOKS_ENTITY_TYPE_BY_LEDGER_LABEL, transactionType);
			const entityType = isKnownLabel ? QUICKBOOKS_ENTITY_TYPE_BY_LEDGER_LABEL[transactionType] : null;
			const canReadTransaction = !!entityType && READABLE_QUICKBOOKS_ENTITY_TYPES.includes(entityType);
			const canListAttachments = !!entityType;
			const isOpenable = canReadTransaction || canListAttachments;
			if (isOpenable) tabs.push({ key: "transaction", label: BOOKS_TAB_LABELS.transaction, transactionType, transactionId, canReadTransaction, canListAttachments });
		}

		const documentViewer = firstViewerOfKind(VIEWER_KIND.document);
		if (documentViewer) {
			const driveFileId = String(documentViewer.args.driveFileId);
			const rowValues = documentViewer.row.values || {};
			const fileName = rowValues.name || null;
			const driveUrl = `${DRIVE_FILE_ADDRESS}${encodeURIComponent(driveFileId)}/view`;
			tabs.push({ key: "document", label: BOOKS_TAB_LABELS.document, driveFileId, fileName, driveUrl });
		}
		return tabs;
	},

	/**
	 * The tone and words of one stated figure's check against what the agent read. A figure with no
	 * status was worked out by code, so it is labelled as calculated rather than as checked. A
	 * status this list does not know is shown as not checked, never as found.
	 *
	 * @param {Object} params
	 * @param {String|null} params.status
	 * @return {Object} { tone: "verified" | "failing" | "code" | "none", label }
	 */
	describeFactStatus({ status }) {
		if (status === null || status === undefined) return CALCULATED_BY_CODE;
		const isKnownStatus = Object.prototype.hasOwnProperty.call(FACT_STATUS_DESCRIPTIONS, status);
		const description = isKnownStatus ? FACT_STATUS_DESCRIPTIONS[status] : UNKNOWN_STATUS;
		return description;
	},

	/**
	 * The line a live tab shows above figures read from QuickBooks now, such as "From QuickBooks,
	 * as of Oct 29, 3:05 pm", or null when the read time is not known.
	 *
	 * @param {Object} params
	 * @param {String|null} params.asOf
	 * @return {String|null}
	 */
	describeAsOf({ asOf }) {
		if (!asOf) return null;
		const readAt = moment(asOf).format(AS_OF_FORMAT);
		const line = `From QuickBooks, as of ${readAt}`;
		return line;
	},

	/**
	 * One cited ledger line's change since the run, as a sentence: its amount ("Amount was $420.00
	 * when the agent read it, now $480.00."), its date, or that it is gone. Gone means it is no
	 * longer in this account's ledger for the period read: deleted, moved to another account or
	 * re-dated out of the period, which one ledger cannot tell apart. Null for any other change.
	 *
	 * @param {Object} params
	 * @param {Object} params.change { rowId, change: "amount" | "date" | "gone", then: { amount, date }, now: { amount, date } | null }
	 * @return {String|null}
	 */
	describeChangeSinceRun({ change }) {
		if (change.change === "gone") return "No longer in this account for this period.";
		if (change.change === "amount") {
			const amountThen = UtilityService.formatSignedCurrency({ value: change.then.amount });
			const amountNow = UtilityService.formatSignedCurrency({ value: change.now.amount });
			const amountSentence = `Amount was ${amountThen} when the agent read it, now ${amountNow}.`;
			return amountSentence;
		}
		if (change.change === "date") {
			const dateThen = moment(change.then.date).format(CHANGE_DATE_FORMAT);
			const dateNow = moment(change.now.date).format(CHANGE_DATE_FORMAT);
			const dateSentence = `Dated ${dateThen} when the agent read it, now ${dateNow}.`;
			return dateSentence;
		}
		return null;
	},
};

export default PlaybookBooksService;
